"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calendar, ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Clock, UserCheck, Mail, Phone, MapPin, Sparkles, Loader } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const reassurancePoints = ["Direct 1-on-1 access to me, Yassine Essaadi", "Tailored search growth strategy, zero generic templates", "100% honest audit & recommendations with zero obligation", "Actionable quick wins you can implement immediately"];
function ConsultationFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-muted/50 rounded-xl border border-border">
        <div className="w-12 h-12 rounded-full bg-accent/20 text-accent flex items-center justify-center">
          <CheckCircle2 className="w-6 h-6" />
        </div>
        <h3 className="text-xl font-bold font-serif text-foreground">
          Consultation Request Received!
        </h3>
        <p className="text-muted-foreground text-sm max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-2 transition-colors duration-200">
          Book Another Session
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <FormField control={control} name="name" rules={{
      required: "Please enter your full name",
      minLength: {
        value: 2,
        message: "Name must be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">
              Your Full Name *
            </FormLabel>
            <FormControl>
              <Input placeholder="e.g. John Smith" className="bg-background text-foreground border-input focus:border-primary px-3 py-2 text-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Please enter your business email",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">
              Work Email Address *
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="you@company.com" className="bg-background text-foreground border-input focus:border-primary px-3 py-2 text-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="website" rules={{
      required: false
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">
              Website URL (Optional)
            </FormLabel>
            <FormControl>
              <Input placeholder="https://yourwebsite.com" className="bg-background text-foreground border-input focus:border-primary px-3 py-2 text-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: false
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium text-sm">
              What is your primary SEO goal? (Optional)
            </FormLabel>
            <FormControl>
              <Input placeholder="e.g. Local visibility, international growth, technical audit" className="bg-background text-foreground border-input focus:border-primary px-3 py-2 text-sm" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 text-destructive text-xs rounded-md border border-destructive/20">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-accent text-accent-foreground hover:bg-accent/90 shadow-md transition-[color,background-color,box-shadow] duration-200 py-6 text-base font-semibold rounded-lg mt-2 cursor-pointer flex items-center justify-center gap-2">
        {isSubmitting ? <>
            <Loader className="animate-spin w-5 h-5" />
            <span>Reserving Your Slot...</span>
          </> : <>
            <SafeHydrate><Calendar className="w-5 h-5" /></SafeHydrate>
            <span>Book a Free SEO Consultation</span>
            <ArrowRight className="w-4 h-4 ml-1" />
          </>}
      </Button>

      <p className="text-xs text-muted-foreground text-center mt-3">
        No sales pressure or automated pitch decks. 100% free, confidential &amp; tailored.
      </p>
    </Form>;
}
export default function Consultation() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="consultationcta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 border-t border-border overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          {}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 space-y-8">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tracking-wide border border-border">
              <UserCheck className="w-4 h-4 text-primary" />
              <span>Direct Independent Consultancy</span>
            </div>

            <div className="space-y-4">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-serif text-foreground leading-tight tracking-tight">
                Let{"'"}s Talk Growth: Book a Free SEO Consultation
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl font-sans">
                When you schedule a strategy session, you work directly with me—Yassine Essaadi.
                No junior account managers, no outsourced agency bloat, and no sales scripts.
                Just honest, expert SEO guidance built around your specific business objectives.
              </p>
            </div>

            {}
            <div className="space-y-3 pt-2">
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider font-sans">
                What You Can Expect:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {reassurancePoints.map((point, index) => <div key={index} data-index={index} className="flex items-start gap-2.5 p-2.5 rounded-lg bg-card/60 border border-border/60">
                    <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm text-foreground/90 font-medium leading-snug">
                      {point}
                    </span>
                  </div>)}
              </div>
            </div>

            {}
            <div className="flex flex-wrap items-center gap-6 pt-4 border-t border-border/80">
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
                <Clock className="w-4 h-4 text-accent" />
                <span>30-Minute Live Strategy</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
                <ShieldCheck className="w-4 h-4 text-accent" />
                <span>Zero Sales Obligation</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm font-medium">
                <Sparkles className="w-4 h-4 text-accent" />
                <span>UK &amp; International Coverage</span>
              </div>
            </div>

            {}
            <div className="bg-card border border-border rounded-xl p-5 shadow-xs space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Prefer to email or call directly?
              </p>
              <div className="flex flex-wrap items-center gap-y-2 gap-x-6 text-sm text-foreground font-medium">
                <a href="mailto:contact@ye-consultancy.com" className="flex items-center gap-2 hover:text-primary transition-colors duration-200">
                  <Mail className="w-4 h-4 text-primary" />
                  <span>contact@ye-consultancy.com</span>
                </a>
                <a href="tel:+447490984430" className="flex items-center gap-2 hover:text-primary transition-colors duration-200">
                  <Phone className="w-4 h-4 text-primary" />
                  <span>+44 7490984430</span>
                </a>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <span>Windmill Hill Business Park, Whitehill Way, Swindon SN5 6QR</span>
              </div>
            </div>
          </motion.div>

          {}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.15,
          ease: "easeOut"
        }} className="lg:col-span-5">
            <Card className="shadow-lg border-border bg-card relative overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-primary via-accent to-primary" />
              <CardContent className="p-6 sm:p-8 space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold uppercase tracking-wider text-accent">
                      Free Session
                    </span>
                    <span className="text-xs text-muted-foreground font-medium">
                      Direct Consultation
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold font-serif text-foreground">
                    Reserve Your Slot
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Fill out the form below to request a 1-on-1 strategy call with Yassine.
                  </p>
                </div>

                <FormProvider className="consultation-form-wrapper" formId="consultation-cta-form" sectionName="consultationcta" defaultValues={{
                name: "",
                email: "",
                website: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                }
              }} submitText="Book Free Consultation" successMessage="Thank you for reaching out! I'll review your details and confirm our strategy session within 24 business hours.">
                  <ConsultationFormInner />
                </FormProvider>

                <div className="text-center pt-2 border-t border-border/60">
                  <Link to="/ContactPage" className="inline-flex items-center text-xs font-medium text-primary hover:text-primary/80 transition-colors duration-200 gap-1">
                    <span>Have a detailed request? Go to the main contact page</span>
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}